import { heure } from "./api.js";
import { Ico, Point } from "./commun.js";

/**
 * Le fil de la soirée : une ligne par tribu (ville) ou par table
 * (événement), sur une frise horaire. Inscriptions, chat, rendez-vous,
 * réunion ; le trait blanc, c'est maintenant. Les deux espaces de la régie
 * le dessinent de la même façon : seules les lignes changent.
 */

export interface LigneFil {
  id: string;
  couleur: string;
  nom: string;
  /** Sous le nom : les humains (« 3 / min. 4 », « 5 · 2 arrivés »). */
  sous: string;
  /** Début des inscriptions. */
  ouverte: number;
  /** Ouverture du chat (réelle, ou prévue). */
  chatA: number;
  chatOuvert: boolean;
  /** Personne inscrit : les inscriptions en pointillé. */
  personne: boolean;
  /** Fin du chat : le rendez-vous en ville, plus tard dans un événement. */
  finChat: number;
  rdvAt: number;
  reunieA: number | null;
  /** Sera annulée si rien ne change. */
  menacee: boolean;
  /** Signalements : à leur heure si on la connaît, sinon au bout de la ligne. */
  signalements: number[];
}

export interface GroupeFil {
  titre: string;
  lignes: LigneFil[];
}

const H = 3_600_000;

export function Fil(props: { groupes: GroupeFil[]; maintenant: number; debut?: number; fin?: number; vide: string; onLigne: (id: string) => void }): JSX.Element {
  const { maintenant } = props;
  const toutes = props.groupes.flatMap((g) => g.lignes);

  // Les bornes : de la plus ancienne ouverture (arrondie à l'heure) à une
  // heure après le dernier rendez-vous — trois heures au moins, douze au plus.
  const debuts = [...toutes.map((l) => l.ouverte), ...(props.debut !== undefined ? [props.debut] : [])];
  const fins = [...toutes.map((l) => Math.max(l.rdvAt, l.finChat) + (l.finChat > l.rdvAt ? 0 : H)), ...(props.fin !== undefined ? [props.fin] : [])];
  let t0 = Math.floor(Math.min(maintenant - H, ...debuts) / H) * H;
  let t1 = Math.ceil(Math.max(maintenant + H, ...fins) / H) * H;
  if (t1 - t0 < 3 * H) t1 = t0 + 3 * H;
  if (t1 - t0 > 12 * H) t0 = t1 - 12 * H;

  const G = 200; // la colonne des noms
  const L = 860; // la largeur de la frise
  const x = (ms: number): number => G + ((Math.min(t1, Math.max(t0, ms)) - t0) / (t1 - t0)) * L;
  const heures: number[] = [];
  for (let h = t0; h <= t1; h += H) heures.push(h);

  const elements: JSX.Element[] = [];
  let y = 50;
  for (const g of props.groupes) {
    if (g.lignes.length === 0) continue;
    elements.push(
      <div key={`q-${g.titre}`} className="quartier" style={{ top: y }}>
        {g.titre}
      </div>,
    );
    y += 26;
    for (const l of g.lignes) {
      const cy = y + 20;
      elements.push(
        <button key={`n-${l.id}`} type="button" className="ligne" style={{ top: y }} onClick={() => props.onLigne(l.id)}>
          <Point couleur={l.couleur} />
          <b>{l.nom}</b>
          <span className="sub">{l.sous}</span>
        </button>,
      );
      // Inscriptions : de l'ouverture au chat.
      elements.push(<div key={`i-${l.id}`} className={l.personne ? "seg-i prevu" : "seg-i"} style={{ top: cy - 6, left: x(l.ouverte), width: Math.max(4, x(l.chatA) - x(l.ouverte)) }} />);
      // Chat : de son ouverture à sa fin (prévu, ou menacé).
      elements.push(
        <div
          key={`c-${l.id}`}
          className={l.menacee ? "seg-c annule" : l.chatOuvert ? "seg-c" : "seg-c prevu"}
          style={{ top: cy - 9, left: x(l.chatA), width: Math.max(4, x(l.finChat) - x(l.chatA)) }}
        />,
      );
      if (l.reunieA) {
        elements.push(<div key={`r-${l.id}`} className="seg-r" style={{ top: cy - 9, left: x(l.reunieA), width: Math.max(8, x(Math.max(maintenant, l.reunieA + 30 * 60_000)) - x(l.reunieA)) }} />);
      }
      elements.push(<span key={`d-${l.id}`} className="rdv" style={{ top: cy - 8, left: x(l.rdvAt) }} />);
      if (l.menacee) {
        elements.push(
          <span key={`a-${l.id}`} className="mark" aria-label="Sera annulée" style={{ top: cy - 13, left: x(l.chatA) }}>
            !
          </span>,
        );
      }
      l.signalements.forEach((a, i) => {
        elements.push(
          <span key={`s-${l.id}-${i}`} className="mark" aria-label="Signalement" style={{ top: cy - 13, left: x(a) }}>
            <Ico nom="signal" />
          </span>,
        );
      });
      y += 40;
    }
  }
  const hauteur = Math.max(y + 16, 160);

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <div className="fil" style={{ height: hauteur, width: G + L + 30 }}>
          {heures.map((h) => (
            <div key={h}>
              <div className="h" style={{ left: x(h) }} />
              <span className="hl" style={{ left: x(h) }}>
                {heure(h)}
              </span>
            </div>
          ))}
          <div className="now" style={{ left: x(maintenant) }} />
          <span className="nowl" style={{ left: x(maintenant) }}>
            {heure(maintenant)}
          </span>
          <div style={{ position: "absolute", top: 40, left: 0, right: 0, height: 1, background: "#2A2A2A" }} />
          {elements}
          {toutes.length === 0 ? (
            <div className="sub" style={{ position: "absolute", top: 60, left: 0 }}>
              {props.vide}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

export function LegendeFil(): JSX.Element {
  return (
    <div className="legende">
      <span>
        <i style={{ width: 30, height: 12, border: "2px solid #7A7A7A", borderRadius: 6 }} />
        Inscriptions
      </span>
      <span>
        <i style={{ width: 30, height: 16, background: "#A3A3A3", borderRadius: 4 }} />
        Chat
      </span>
      <span>
        <i style={{ width: 14, height: 14, borderRadius: "50%", background: "#FFFFFF" }} />
        Rendez-vous
      </span>
      <span>
        <i style={{ width: 30, height: 16, background: "#FFFFFF", borderRadius: 4 }} />
        Réunie
      </span>
      <span>
        <i style={{ width: 30, height: 16, border: "2px dashed #FFFFFF", borderRadius: 4 }} />
        Sera annulée si rien ne change
      </span>
    </div>
  );
}
